<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
	modelValue: { default: '' },
	label: { type: String, default: '' },
	id: { type: String, default: '' },
	options: { type: Array, default: () => [] },
	hasError: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	// Which surface the field sits on. '1' (default) keeps the original auth-page
	// look; '2' renders a filled variant that reads cleanly on bg-surface-2 panels
	// (e.g. the side sliders).
	surface: { type: String, default: '1' },
	// Compact variant: shorter height / tighter padding / smaller text. For dense
	// contexts like modals and side sliders where the full 56px field feels heavy.
	dense: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'change'])
const focused = ref(false)

const active = computed(
	() => focused.value || (props.modelValue !== '' && props.modelValue != null),
)

function normalize(option) {
	if (typeof option === 'object' && option !== null) {
		return {
			value: option.value,
			text: option.text ?? option.label ?? String(option.value),
		}
	}
	return { value: option, text: String(option) }
}
const normalizedOptions = computed(() => props.options.map(normalize))
</script>

<template>
	<div
		class="ff"
		:class="{ 'ff--active': active, 'ff--focus': focused, 'ff--error': hasError, 'ff--s2': surface === '2', 'ff--dense': dense }"
	>
		<select
			:id="id"
			class="ff__select"
			:class="{ 'ff__select--empty': modelValue === '' || modelValue == null }"
			:value="modelValue"
			:disabled="disabled"
			@change="
				emit('update:modelValue', $event.target.value);
				emit('change', $event)
			"
			@focus="focused = true"
			@blur="focused = false"
		>
			<option
				v-for="option in normalizedOptions"
				:key="option.value"
				:value="option.value"
			>
				{{ option.text }}
			</option>
		</select>
		<label :for="id" class="ff__label">{{ label }}</label>
		<svg
			class="ff__chevron"
			width="12"
			height="8"
			viewBox="0 0 12 8"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M1 1.5L6 6.5L11 1.5"
				stroke="currentColor"
				stroke-width="1.6"
				stroke-linecap="round"
				stroke-linejoin="round"
			/>
		</svg>
	</div>
</template>

<style scoped>
.ff {
	position: relative;
	width: 100%;
}
.ff__select {
	width: 100%;
	height: 56px;
	padding: 18px 36px 2px 14px;
	border-radius: 12px;
	background-color: var(--castr-field);
	border: 1px solid var(--castr-border);
	color: var(--castr-text);
	font-size: 15px;
	outline: none;
	appearance: none;
	-webkit-appearance: none;
	cursor: pointer;
	/* Renders the browser-native options popup dark instead of OS white. */
	color-scheme: dark;
	transition:
		border-color 0.15s ease,
		box-shadow 0.15s ease;
}
.ff__select--empty {
	color: transparent;
}
.ff--active .ff__select--empty {
	color: var(--castr-text-muted);
}
.ff__select:hover:not(:focus) {
	border-color: var(--castr-border-strong);
}
.ff__select:focus {
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
.ff__select option {
	color: var(--castr-text);
	background-color: var(--castr-field-solid);
}
.ff__label {
	position: absolute;
	left: 15px;
	top: 50%;
	transform: translateY(-50%);
	color: var(--castr-text-muted);
	font-size: 15px;
	pointer-events: none;
	transition:
		top 0.15s ease,
		font-size 0.15s ease,
		color 0.15s ease,
		transform 0.15s ease;
}
.ff--active .ff__label {
	top: 9px;
	transform: none;
	font-size: 11px;
	font-weight: 500;
	letter-spacing: 0.02em;
	color: var(--castr-text-muted);
}
.ff--focus .ff__label {
	color: var(--castr-blue-soft);
}
.ff__chevron {
	position: absolute;
	right: 14px;
	top: 50%;
	transform: translateY(-50%);
	color: var(--castr-text-muted);
	pointer-events: none;
}
.ff--error .ff__select {
	border-color: var(--castr-danger);
}
/* Filled variant for fields placed on a bg-surface-2 panel (side sliders).
   Uses surface-3 like the surrounding form controls so it reads cleanly. */
.ff--s2 .ff__select {
	background-color: var(--surface-3, #343e61);
	border-color: var(--surface-4, #404b72);
}
.ff--s2 .ff__select:hover:not(:focus) {
	border-color: var(--surface-5, #4f5c87);
}
.ff--s2 .ff__select option {
	background-color: var(--surface-3, #343e61);
}
/* Compact variant — shorter, tighter fields for dense contexts (modals/sliders). */
.ff--dense .ff__select {
	height: 44px;
	padding: 14px 34px 2px 12px;
	border-radius: 8px;
	font-size: 14px;
}
.ff--dense .ff__label {
	left: 13px;
	font-size: 14px;
}
.ff--dense.ff--active .ff__label {
	top: 6px;
	font-size: 10px;
}
/* iOS Safari zooms focused selects under 16px and breaks mobile modal layout. */
@media (max-width: 767px) {
	.ff__select,
	.ff--dense .ff__select {
		font-size: 16px;
	}
}
</style>
